import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

export interface MappingProgressProps {
  mappedCount: number;
  totalCount: number;
  requiredMappedCount: number;
  requiredTotalCount: number;
  missingCount: number; // New prop for missing properties
  extraCount: number; // New prop for extra properties
  mismatchCount: number; // New prop for type mismatch properties
  className?: string;
  baselineSchema?: 'input' | 'output';
}

const MappingProgressBar: React.FC<MappingProgressProps> = ({
  mappedCount,
  totalCount,
  requiredMappedCount,
  requiredTotalCount,
  missingCount, // Destructure new props
  extraCount, // Destructure new props
  mismatchCount, // Destructure new props
  className,
  baselineSchema
}) => {
  // Calculate percentages for each segment
  const mappedPercentage = totalCount > 0 ? (mappedCount / totalCount) * 100 : 0;
  const missingPercentage = totalCount > 0 ? (missingCount / totalCount) * 100 : 0;
  const extraPercentage = totalCount > 0 ? (extraCount / totalCount) * 100 : 0;
  const mismatchPercentage = totalCount > 0 ? (mismatchCount / totalCount) * 100 : 0;

  // Ensure percentages sum up to 100% due to potential rounding issues or if totalCount is 0
  const totalSegmentedPercentage = mappedPercentage + missingPercentage + extraPercentage + mismatchPercentage;
  const remainingPercentage = 100 - totalSegmentedPercentage;

  // Adjust one segment (e.g., missing) to account for rounding differences if needed
  const adjustedMissingPercentage = missingPercentage + remainingPercentage;

  const requiredPercentage = requiredTotalCount > 0 ? Math.round((requiredMappedCount / requiredTotalCount) * 100) : 100;
  
  // Determine color based on completion percentage for required fields
  const getRequiredColorClass = (percentage: number) => {
    if (percentage < 30) return 'bg-red-500';
    if (percentage < 70) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  const requiredBarColor = getRequiredColorClass(requiredPercentage);
  
  return (
    <div className={clsx("flex flex-col space-y-2 w-full", className)}>
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {baselineSchema === 'input' ? 'Input Mapping Progress' : baselineSchema === 'output' ? 'Output Mapping Progress' : 'Mapping Progress'}
          </span>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">{mappedCount}</span>
          <span className="mx-1">/</span>
          <span>{totalCount}</span>
        </div>
      </div>
      
      {/* Overall segmented progress bar */}
      <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
        {mappedCount > 0 && (
          <motion.div 
            className="h-full bg-emerald-500" // Mapped: Green
            initial={{ width: 0 }}
            animate={{ width: `${mappedPercentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        )}
        {missingCount > 0 && (
          <motion.div 
            className="h-full bg-red-500" // Missing: Red
            initial={{ width: 0 }}
            animate={{ width: `${adjustedMissingPercentage}%` }} // Use adjusted percentage
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        )}
        {extraCount > 0 && (
          <motion.div 
            className="h-full bg-blue-500" // Extra: Blue
            initial={{ width: 0 }}
            animate={{ width: `${extraPercentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        )}
        {mismatchCount > 0 && (
          <motion.div 
            className="h-full bg-yellow-500" // Mismatch: Yellow
            initial={{ width: 0 }}
            animate={{ width: `${mismatchPercentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        )}
      </div>
      
      {/* Required fields progress section */}
      {requiredTotalCount > 0 && (
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Required Fields</span>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium">{requiredMappedCount}</span>
            <span className="mx-1">/</span>
            <span>{requiredTotalCount}</span>
          </div>
        </div>
      )}
      
      {/* Required fields progress bar */}
      {requiredTotalCount > 0 && (
        <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <motion.div 
            className={clsx("h-full rounded-full", requiredBarColor)}
            initial={{ width: 0 }}
            animate={{ width: `${requiredPercentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      )}
    </div>
  );
};

export default MappingProgressBar;
